import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../services/api';
import { Subscription } from '../types/models';
import { useTranslation } from 'react-i18next';
import SocialMediaSetupCheck from './auth/SocialMediaSetupCheck';

interface SubscriptionCheckProps {
  children: React.ReactNode;
}

const SubscriptionCheck: React.FC<SubscriptionCheckProps> = ({ children }) => {
  const { business, loading: authLoading } = useAuth();
  const location = useLocation();
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { i18n, t } = useTranslation('dashboard');
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!business?._id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await subscriptionAPI.getSubscription(business._id);
        
        if (response.data?.subscription) {
          const sub = response.data.subscription;
          // Check if subscription is active
          if (sub.status === 'active') {
            setSubscription(sub);
          }
        }
      } catch (err: any) {
        // If subscription not found (404), user doesn't have a subscription
        if (err.response?.status !== 404) {
          console.error('Error checking subscription:', err);
          setError(t('subscriptionCheck.error'));
        }
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && business?._id) {
      checkSubscription();
    } else if (!authLoading && !business) {
      setLoading(false);
    }
  }, [business, authLoading]);

  // Show loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70">{t('subscriptionCheck.loading')}</p>
        </div>
      </div>
    );
  }

  // If error occurred, still allow access (fallback)
  if (error) {
    console.error(error);
  }

  // If no subscription or subscription not active, redirect to checkout
  if (business?._id && !subscription) {
    // Extract language from pathname or use i18n language
    const pathMatch = location.pathname.match(/^\/(tr|en)/);
    const currentLang = pathMatch ? pathMatch[1] : (i18n.language || 'tr');
    return <Navigate to={`/${currentLang}/checkout`} replace />;
  }

  // If has active subscription, check social media setup before rendering children
  return (
    <SocialMediaSetupCheck>
      {children}
    </SocialMediaSetupCheck>
  );
};

export default SubscriptionCheck;

